export const PROCEDURE_ITEM_TYPES = [
  { value: "text", label: "Text" },
  { value: "checkbox", label: "Checkbox" },
  { value: "number", label: "Numeric" },
  { value: "choice", label: "Single Select" },
  { value: "photo", label: "Photo" },
] as const;

export type ProcedureItemType = (typeof PROCEDURE_ITEM_TYPES)[number]["value"];

export function normalizeChoiceOptions(options: string[]): string[] {
  return options.map((option) => option.trim()).filter(Boolean);
}

export type ProcedureItem = {
  id: string;
  label: string;
  description: string;
  type: ProcedureItemType;
  required: boolean;
  options: string[];
  unit: string;
};

export type ProcedureSection = {
  id: string;
  title: string;
  items: ProcedureItem[];
};

export type ProcedureGroup = {
  id: string;
  title: string;
  sections: ProcedureSection[];
};

export type ProcedureAuthoringDraft = {
  id?: string;
  title: string;
  description: string;
  category: string;
  categories: string[];
  categoryIds: number[];
  owner: string;
  ownerId: number | null;
  team: string;
  teamId: number | null;
  groups: ProcedureGroup[];
  looseSections: ProcedureSection[];
  looseItems: ProcedureItem[];
};

export function createItem(
  id: string,
  type: ProcedureItemType = "text",
): ProcedureItem {
  return {
    id,
    label: "",
    description: "",
    type,
    required: true,
    options: type === "choice" ? ["Option 1"] : [],
    unit: "",
  };
}

export function createBlankProcedure(
  idFactory: () => string,
): ProcedureAuthoringDraft {
  return {
    title: "",
    description: "",
    category: "",
    categories: [],
    categoryIds: [],
    owner: "",
    ownerId: null,
    team: "",
    teamId: null,
    groups: [],
    looseSections: [],
    looseItems: [createItem(idFactory(), "checkbox")],
  };
}

export function updateItem(
  draft: ProcedureAuthoringDraft,
  id: string,
  update: (item: ProcedureItem) => ProcedureItem,
): ProcedureAuthoringDraft {
  const updateList = (items: ProcedureItem[]) =>
    items.map((item) => (item.id === id ? update(item) : item));
  return {
    ...draft,
    looseItems: updateList(draft.looseItems),
    looseSections: draft.looseSections.map((section) => ({
      ...section,
      items: updateList(section.items),
    })),
    groups: draft.groups.map((group) => ({
      ...group,
      sections: group.sections.map((section) => ({
        ...section,
        items: updateList(section.items),
      })),
    })),
  };
}

export function addGroup(
  draft: ProcedureAuthoringDraft,
  group: ProcedureGroup,
): ProcedureAuthoringDraft {
  return { ...draft, groups: [...draft.groups, group] };
}

export function addSection(
  draft: ProcedureAuthoringDraft,
  groupId: string | null,
  section: ProcedureSection,
): ProcedureAuthoringDraft {
  if (!groupId)
    return { ...draft, looseSections: [...draft.looseSections, section] };
  return {
    ...draft,
    groups: draft.groups.map((group) =>
      group.id === groupId
        ? { ...group, sections: [...group.sections, section] }
        : group,
    ),
  };
}

export function duplicateSection(
  draft: ProcedureAuthoringDraft,
  groupId: string | null,
  sectionId: string,
  idFactory: () => string,
): ProcedureAuthoringDraft {
  if (!groupId) {
    const index = draft.looseSections.findIndex(
      (section) => section.id === sectionId,
    );
    if (index < 0) return draft;
    const source = draft.looseSections[index];
    const copy = {
      ...source,
      id: idFactory(),
      title: source.title ? `${source.title} (copy)` : "",
      items: source.items.map((item) => ({
        ...item,
        id: idFactory(),
        options: [...item.options],
      })),
    };
    return {
      ...draft,
      looseSections: [
        ...draft.looseSections.slice(0, index + 1),
        copy,
        ...draft.looseSections.slice(index + 1),
      ],
    };
  }
  return {
    ...draft,
    groups: draft.groups.map((group) => {
      if (group.id !== groupId) return group;
      const index = group.sections.findIndex(
        (section) => section.id === sectionId,
      );
      if (index < 0) return group;
      const source = group.sections[index];
      const copy = {
        ...source,
        id: idFactory(),
        title: source.title ? `${source.title} (copy)` : "",
        items: source.items.map((item) => ({
          ...item,
          id: idFactory(),
          options: [...item.options],
        })),
      };
      return {
        ...group,
        sections: [
          ...group.sections.slice(0, index + 1),
          copy,
          ...group.sections.slice(index + 1),
        ],
      };
    }),
  };
}

export function duplicateGroup(
  draft: ProcedureAuthoringDraft,
  groupId: string,
  idFactory: () => string,
): ProcedureAuthoringDraft {
  const index = draft.groups.findIndex((group) => group.id === groupId);
  if (index < 0) return draft;
  const source = draft.groups[index];
  const copy = {
    ...source,
    id: idFactory(),
    title: source.title ? `${source.title} (copy)` : "",
    sections: source.sections.map((section) => ({
      ...section,
      id: idFactory(),
      items: section.items.map((item) => ({
        ...item,
        id: idFactory(),
        options: [...item.options],
      })),
    })),
  };
  return {
    ...draft,
    groups: [
      ...draft.groups.slice(0, index + 1),
      copy,
      ...draft.groups.slice(index + 1),
    ],
  };
}

export function deleteSectionKeepingContents(
  draft: ProcedureAuthoringDraft,
  groupId: string | null,
  sectionId: string,
): ProcedureAuthoringDraft {
  if (groupId) {
    return {
      ...draft,
      groups: draft.groups.map((group) => {
        if (group.id !== groupId || group.sections.length <= 1) return group;
        const index = group.sections.findIndex(
          (section) => section.id === sectionId,
        );
        if (index < 0) return group;
        const removed = group.sections[index];
        const sections = group.sections.filter(
          (section) => section.id !== sectionId,
        );
        const target = index > 0 ? index - 1 : 0;
        sections[target] = {
          ...sections[target],
          items: [...sections[target].items, ...removed.items],
        };
        return { ...group, sections };
      }),
    };
  }
  if (draft.looseSections.length <= 1) return draft;
  const index = draft.looseSections.findIndex(
    (section) => section.id === sectionId,
  );
  if (index < 0) return draft;
  const removed = draft.looseSections[index];
  const sections = draft.looseSections.filter(
    (section) => section.id !== sectionId,
  );
  const target = index > 0 ? index - 1 : 0;
  sections[target] = {
    ...sections[target],
    items: [...sections[target].items, ...removed.items],
  };
  return { ...draft, looseSections: sections };
}

export function deleteGroupKeepingContents(
  draft: ProcedureAuthoringDraft,
  groupId: string,
): ProcedureAuthoringDraft {
  if (draft.groups.length <= 1) return draft;
  const index = draft.groups.findIndex((group) => group.id === groupId);
  if (index < 0) return draft;
  const removed = draft.groups[index];
  const groups = draft.groups.filter((group) => group.id !== groupId);
  const target = index > 0 ? index - 1 : 0;
  groups[target] = {
    ...groups[target],
    sections: [...groups[target].sections, ...removed.sections],
  };
  return { ...draft, groups };
}

export function reorderGroups(
  draft: ProcedureAuthoringDraft,
  groupId: string,
  direction: -1 | 1,
): ProcedureAuthoringDraft {
  return { ...draft, groups: reorder(draft.groups, groupId, direction) };
}

export function reorderSections(
  draft: ProcedureAuthoringDraft,
  groupId: string | null,
  sectionId: string,
  direction: -1 | 1,
): ProcedureAuthoringDraft {
  if (!groupId)
    return {
      ...draft,
      looseSections: reorder(draft.looseSections, sectionId, direction),
    };
  return {
    ...draft,
    groups: draft.groups.map((group) =>
      group.id === groupId
        ? { ...group, sections: reorder(group.sections, sectionId, direction) }
        : group,
    ),
  };
}

export function addItem(
  draft: ProcedureAuthoringDraft,
  groupId: string | null,
  sectionId: string | null,
  item: ProcedureItem,
): ProcedureAuthoringDraft {
  if (!sectionId) return { ...draft, looseItems: [...draft.looseItems, item] };
  if (!groupId)
    return {
      ...draft,
      looseSections: draft.looseSections.map((section) =>
        section.id === sectionId
          ? { ...section, items: [...section.items, item] }
          : section,
      ),
    };
  return {
    ...draft,
    groups: draft.groups.map((group) =>
      group.id !== groupId
        ? group
        : {
            ...group,
            sections: group.sections.map((section) =>
              section.id === sectionId
                ? { ...section, items: [...section.items, item] }
                : section,
            ),
          },
    ),
  };
}

export function insertItemAfter(
  draft: ProcedureAuthoringDraft,
  groupId: string | null,
  sectionId: string | null,
  afterItemId: string,
  item: ProcedureItem,
): ProcedureAuthoringDraft {
  const insert = (items: ProcedureItem[]) => {
    const index = items.findIndex((candidate) => candidate.id === afterItemId);
    if (index < 0) return items;
    return [...items.slice(0, index + 1), item, ...items.slice(index + 1)];
  };
  if (!sectionId) return { ...draft, looseItems: insert(draft.looseItems) };
  if (!groupId) {
    return {
      ...draft,
      looseSections: draft.looseSections.map((section) =>
        section.id === sectionId
          ? { ...section, items: insert(section.items) }
          : section,
      ),
    };
  }
  return {
    ...draft,
    groups: draft.groups.map((group) => ({
      ...group,
      sections: group.sections.map((section) =>
        group.id === groupId && section.id === sectionId
          ? { ...section, items: insert(section.items) }
          : section,
      ),
    })),
  };
}

function reorder<T extends { id: string }>(
  items: T[],
  id: string,
  direction: -1 | 1,
): T[] {
  const index = items.findIndex((item) => item.id === id);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function reorderItems(
  draft: ProcedureAuthoringDraft,
  sectionId: string | null,
  itemId: string,
  direction: -1 | 1,
): ProcedureAuthoringDraft {
  if (!sectionId)
    return {
      ...draft,
      looseItems: reorder(draft.looseItems, itemId, direction),
    };
  if (
    !draft.groups.some((group) =>
      group.sections.some((section) => section.id === sectionId),
    )
  )
    return {
      ...draft,
      looseSections: draft.looseSections.map((section) =>
        section.id === sectionId
          ? { ...section, items: reorder(section.items, itemId, direction) }
          : section,
      ),
    };
  return {
    ...draft,
    groups: draft.groups.map((group) => ({
      ...group,
      sections: group.sections.map((section) =>
        section.id === sectionId
          ? { ...section, items: reorder(section.items, itemId, direction) }
          : section,
      ),
    })),
  };
}

export function moveItemBefore(
  draft: ProcedureAuthoringDraft,
  sectionId: string | null,
  itemId: string,
  targetId: string,
): ProcedureAuthoringDraft {
  const move = (items: ProcedureItem[]) => {
    const from = items.findIndex((item) => item.id === itemId);
    const target = items.findIndex((item) => item.id === targetId);
    if (from < 0 || target < 0 || from === target) return items;
    const next = [...items];
    const [item] = next.splice(from, 1);
    next.splice(
      next.findIndex((candidate) => candidate.id === targetId),
      0,
      item,
    );
    return next;
  };
  if (!sectionId) return { ...draft, looseItems: move(draft.looseItems) };
  return {
    ...draft,
    looseSections: draft.looseSections.map((section) =>
      section.id === sectionId
        ? { ...section, items: move(section.items) }
        : section,
    ),
    groups: draft.groups.map((group) => ({
      ...group,
      sections: group.sections.map((section) =>
        section.id === sectionId
          ? { ...section, items: move(section.items) }
          : section,
      ),
    })),
  };
}

export function moveItemToSectionBefore(
  draft: ProcedureAuthoringDraft,
  sourceSectionId: string | null,
  targetSectionId: string | null,
  itemId: string,
  targetItemId?: string,
): ProcedureAuthoringDraft {
  const sourceItems = sourceSectionId
    ? (draft.looseSections.find((section) => section.id === sourceSectionId)
        ?.items ??
      draft.groups
        .flatMap((group) => group.sections)
        .find((section) => section.id === sourceSectionId)?.items)
    : draft.looseItems;
  const item = sourceItems?.find((candidate) => candidate.id === itemId);
  if (!item) return draft;

  const targetItems = targetSectionId
    ? (draft.looseSections.find((section) => section.id === targetSectionId)
        ?.items ??
      draft.groups
        .flatMap((group) => group.sections)
        .find((section) => section.id === targetSectionId)?.items)
    : draft.looseItems;
  if (!targetItems) return draft;
  if (
    targetItemId &&
    !targetItems.some((candidate) => candidate.id === targetItemId)
  )
    return draft;
  if (itemId === targetItemId && sourceSectionId === targetSectionId)
    return draft;

  const withoutItem = deleteItem(draft, sourceSectionId, itemId);
  const insert = (items: ProcedureItem[]) => {
    const targetIndex = targetItemId
      ? items.findIndex((candidate) => candidate.id === targetItemId)
      : items.length;
    if (targetIndex < 0) return items;
    return [...items.slice(0, targetIndex), item, ...items.slice(targetIndex)];
  };

  if (!targetSectionId)
    return { ...withoutItem, looseItems: insert(withoutItem.looseItems) };

  let inserted = false;
  const looseSections = withoutItem.looseSections.map((section) => {
    if (section.id !== targetSectionId) return section;
    inserted = true;
    return { ...section, items: insert(section.items) };
  });
  const groups = withoutItem.groups.map((group) => ({
    ...group,
    sections: group.sections.map((section) => {
      if (section.id !== targetSectionId) return section;
      inserted = true;
      return { ...section, items: insert(section.items) };
    }),
  }));
  if (!inserted) return draft;
  return { ...withoutItem, groups, looseSections };
}

export function duplicateItem(
  draft: ProcedureAuthoringDraft,
  sectionId: string | null,
  itemId: string,
  idFactory: () => string,
): ProcedureAuthoringDraft {
  const duplicate = (items: ProcedureItem[]) => {
    const index = items.findIndex((item) => item.id === itemId);
    if (index < 0) return items;
    const copy = {
      ...items[index],
      id: idFactory(),
      label: items[index].label ? `${items[index].label} (copy)` : "",
      options: [...items[index].options],
    };
    return [...items.slice(0, index + 1), copy, ...items.slice(index + 1)];
  };
  if (!sectionId) return { ...draft, looseItems: duplicate(draft.looseItems) };
  if (
    !draft.groups.some((group) =>
      group.sections.some((section) => section.id === sectionId),
    )
  )
    return {
      ...draft,
      looseSections: draft.looseSections.map((section) =>
        section.id === sectionId
          ? { ...section, items: duplicate(section.items) }
          : section,
      ),
    };
  return {
    ...draft,
    groups: draft.groups.map((group) => ({
      ...group,
      sections: group.sections.map((section) =>
        section.id === sectionId
          ? { ...section, items: duplicate(section.items) }
          : section,
      ),
    })),
  };
}

export function deleteItem(
  draft: ProcedureAuthoringDraft,
  sectionId: string | null,
  itemId: string,
): ProcedureAuthoringDraft {
  if (!sectionId)
    return {
      ...draft,
      looseItems: draft.looseItems.filter((item) => item.id !== itemId),
    };
  if (
    !draft.groups.some((group) =>
      group.sections.some((section) => section.id === sectionId),
    )
  )
    return {
      ...draft,
      looseSections: draft.looseSections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              items: section.items.filter((item) => item.id !== itemId),
            }
          : section,
      ),
    };
  return {
    ...draft,
    groups: draft.groups.map((group) => ({
      ...group,
      sections: group.sections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              items: section.items.filter((item) => item.id !== itemId),
            }
          : section,
      ),
    })),
  };
}

export function validateProcedure(
  draft: ProcedureAuthoringDraft,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!draft.title.trim()) errors.title = "Enter a procedure title.";
  draft.groups.forEach((group) => {
    if (!group.title.trim())
      errors[`heading:${group.id}`] = "Enter a heading title.";
    if (group.sections.length === 0)
      errors[`heading:${group.id}`] = "Add a section to this heading.";
    group.sections.forEach((section) => {
      if (!section.title.trim())
        errors[`section:${section.id}`] = "Enter a section title.";
    });
  });
  draft.looseSections.forEach((section) => {
    if (!section.title.trim())
      errors[`section:${section.id}`] = "Enter a section title.";
  });
  const items = [
    ...draft.looseItems,
    ...draft.looseSections.flatMap((section) => section.items),
    ...draft.groups.flatMap((group) =>
      group.sections.flatMap((section) => section.items),
    ),
  ];
  items.forEach((item) => {
    if (!item.label.trim()) errors[item.id] = "Enter an item label.";
    if (item.type === "choice") {
      const options = item.options
        .map((option) => option.trim())
        .filter(Boolean);
      if (options.length < 1) errors[item.id] = "Add at least one choice.";
      else if (new Set(options).size !== options.length)
        errors[item.id] = "Choice options must be unique.";
    }
  });
  return errors;
}

export function flattenProcedureItems(
  draft: ProcedureAuthoringDraft,
): ProcedureItem[] {
  return [
    ...draft.looseItems,
    ...draft.looseSections.flatMap((section) => section.items),
    ...draft.groups.flatMap((group) =>
      group.sections.flatMap((section) => section.items),
    ),
  ];
}
